/**
 * @name getMetaContentByName
 * @description get meta tags content from document
 * @param {string} tagAttribute
 * @param {string} attributeVal
 * @param {Window} windowContext
 * @returns {string} detected content value or empty string if nothing found
 */
export function getMetaContentByName(
  tagAttribute: string,
  attributeVal: string,
  windowContext: Window = window
): string {
  const metaTag = windowContext.document.querySelector<HTMLMetaElement>(`meta[${tagAttribute}='${attributeVal}']`);
  if (metaTag) {
    return metaTag.content;
  }
  return '';
}

/**
 * @name isElementInView
 * @description checking if element is currently partially in view in browser
 * @param {HTMLElement} el
 * @returns {boolean} true if element is currently partially in view and false if not
 */
export function isElementPartiallyInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();

  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  // http://stackoverflow.com/questions/325933/determinewhethertwodate-ranges-overlap
  const vertInView = rect.top <= windowHeight && rect.top + rect.height >= 0;
  const horInView = rect.left <= windowWidth && rect.left + rect.width >= 0;

  return vertInView && horInView;
}

/**
 * @name isInViewport
 * @description checking if element is currently in view in browser
 * @param {HTMLElement} el
 * @returns {boolean} true if element is currently in view and false if not
 */
export function isInViewport(el: HTMLElement): boolean {
  let r;
  let html;
  if (!el || 1 !== el.nodeType) {
    return false;
  }
  html = document.documentElement;
  r = el.getBoundingClientRect();

  return !!r && r.bottom >= 0 && r.right >= 0 && r.top <= html.clientHeight && r.left <= html.clientWidth;
}

/**
 * Wait for an element to be on the screen and return it once available
 * @param {string} selector Selector for element
 * @param timeout
 * @param maxTries
 * @param windowContext
 */
export async function waitForElement(
  selector: string,
  timeout: number,
  maxTries: number,
  windowContext: Window = window
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    let count = 0;
    const interval = setInterval(() => {
      const element = windowContext.document.querySelector(selector);
      // If element is found, clear interval and resolve promise with it
      if (element) {
        clearInterval(interval);
        resolve(element as HTMLElement);
      }
      // If we've waited for too long and the element is still not on the page, we should clear this interval and resolve with null
      if (count === maxTries) {
        clearInterval(interval);
        resolve(null);
      }
      count++;
    }, timeout);
  });
}

/**
 * @param url
 */
export function prefetchScript(url: string): HTMLLinkElement {
  const prefetchElement = document.createElement('link');
  prefetchElement.href = url;
  prefetchElement.as = 'script';
  prefetchElement.rel = 'prefetch';
  document.head.appendChild(prefetchElement);
  return prefetchElement;
}

/**
 * Utility to load JS file in the document. Appends file to the head of the document
 * @param {string} src - script url
 * @param {object} attributes - attr object to add to script
 * @param windowContext
 * @returns {Promise<string | error>}
 */
export const loadJS = (
  src: string,
  attributes: { [key: string]: string } = {},
  windowContext: Window = window
): Promise<string | Error> =>
  new Promise((resolve, reject) => {
    /**
     * ⚠ NOTE: Scripts that are dynamically created and added to the document are async by default,
     * they don’t block rendering and execute as soon as they download, meaning they could come out in the wrong order.
     * @see https://www.html5rocks.com/en/tutorials/speed/script-loading/
     */
    const scriptTag = windowContext.document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.src = src;
    scriptTag.onload = () => resolve(src);
    scriptTag.onerror = reject;
    scriptTag.async = true; // Despite fact that it's async by default some publishers complain that they don't see 'async' attr. 🤷‍️
    // Set additional scrip attributes
    if (attributes instanceof Object) {
      Object.keys(attributes).forEach((key) => ((scriptTag as any)[key] = attributes[key]));
    }

    try {
      windowContext.document.head.appendChild(scriptTag);
    } catch (err) {
      reject(err);
    }
  });

/**
 * Get element coordinates
 * @param el
 * @param rect
 * @returns
 */
export function getOffset(rect: DOMRect) {
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY,
  };
}

/**
 * Decode HTML entities from texti
 * @param text
 */
export function stripHTMLEntities(text: string) {
  const parser = new DOMParser();
  const decodedText = parser.parseFromString(text, 'text/html').body.textContent;
  return decodedText;
}

/**
 * @name getTextFromHTML
 * @description Get text from HTML string with HTML tags and HTML character codes
 * @param {string} HTMLString - HTML string from which we need to get text
 * @returns {string} - string from rendered html
 */
export function getTextFromHTML(HTMLString: string): string {
  const para = document.createElement('p');
  para.innerHTML = HTMLString;
  return para.innerText.trim();
}

/**
 * Check if platform is inside an iframe
 * @returns {boolean} is inside an iframe
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (_e) {
    return true;
  }
}

/**
 * Check if platform is inside a cross-origin iframe
 * @returns {boolean} is inside a cross-origin iframe
 */
export function isInCrossOriginFrame(): boolean {
  try {
    return !window.top?.location.hostname;
  } catch (_e) {
    return true;
  }
}
